import { useState } from "react";
import { ENTRY_POINT_INDEX } from "../utils/chartFrame";
import { formatTimeHeld, getMultiplier, getTradePnl } from "../utils/tradeMath";

/**
 * Candle under the pointer and the trade at it: the price, P&L and multiplier
 * at its close and how long the trade had been open by then.
 *
 * @param {object} params
 * @param {import("../utils/tradeModel").TradeDetails} params.trade
 * @param {object[]} params.candles all candles of the trade
 * @param {number|null} params.intervalMs
 * @param {boolean} params.isEnabled hovering works once the replay is over
 */
export const useChartHover = ({ trade, candles, intervalMs, isEnabled }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isEntry = hoveredIndex === ENTRY_POINT_INDEX;
  const candle =
    isEnabled && hoveredIndex != null ? candles[hoveredIndex] : null;

  if (!isEnabled || (!candle && !isEntry)) {
    return { hover: null, point: null, setHoveredIndex };
  }

  // The line's first point is the entry itself
  // and a closed trade's last point is its exit with the realized result
  const isExit =
    trade.isClosed && !isEntry && hoveredIndex === candles.length - 1;
  const price = isEntry
    ? trade.entryPrice
    : isExit
      ? trade.exitPrice ?? candle.close
      : candle.close;
  const pnl = isExit
    ? trade.finalPnl ?? getTradePnl(trade, price)
    : getTradePnl(trade, price);
  // The candle's close, the trade's end at the latest
  const pointTime = isEntry
    ? trade.openedAt
    : Math.min(candle.time + intervalMs, trade.closedAt ?? Date.now());

  return {
    hover: {
      index: hoveredIndex,
      price,
      label: formatTimeHeld(trade.openedAt, pointTime),
    },
    point: {
      price,
      pnl,
      multiplier: getMultiplier(trade.wager, pnl),
    },
    setHoveredIndex,
  };
};
